import { useCallback, useEffect, useRef, useState } from "react";
import PreferencesService from "../../../services/preferences.service";

// How many times the Story Studio may pulse the "Stickers" button as a
// first-run nudge before it stays quiet for good. Two opens teaches the
// affordance without nagging repeat sharers.
export const STICKER_HINT_MAX_SHOWS = 2;

// Pure rule: pulse only while we've nudged fewer than the cap.
export function shouldPulseStickerHint(timesShown: number): boolean {
    return timesShown < STICKER_HINT_MAX_SHOWS;
}

// Decides whether to pulse the Stickers button when the editor opens, and
// remembers (via Preferences) how many times we've nudged so it self-extinguishes.
//
//  • `active` is the editor-open flag — evaluated once per open.
//  • The caller invokes `dismiss()` the moment the rider opens the sheet, which
//    stops the pulse and marks the hint as learned so it never fires again.
export function useStickerHint(active: boolean): { pulse: boolean; dismiss: () => void } {
    const [pulse, setPulse] = useState<boolean>(false);
    const evaluatedRef = useRef<boolean>(false);

    useEffect(() => {
        if (!active) {
            // Re-arm for the next open.
            evaluatedRef.current = false;
            setPulse(false);
            return;
        }
        if (evaluatedRef.current) return;
        evaluatedRef.current = true;

        let cancelled = false;
        (async () => {
            const shown = await PreferencesService.getStoryStudioStickerHintCount();
            if (cancelled) return;
            if (shouldPulseStickerHint(shown)) {
                setPulse(true);
                await PreferencesService.setStoryStudioStickerHintCount(shown + 1);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [active]);

    const dismiss = useCallback(() => {
        setPulse(false);
        // Found it — never nudge again.
        PreferencesService.setStoryStudioStickerHintCount(STICKER_HINT_MAX_SHOWS).catch(() => {
            /* best-effort; the pulse is already off for this session */
        });
    }, []);

    return { pulse, dismiss };
}
